import React, { useRef, useEffect } from "react";
import { useFormik } from "formik";
import { useForm, ValidationError } from "@formspree/react";
import { validate } from "./Utils";
import { Button } from "../Button/Button";
import Image from "../../components/Image/Image";
import GreenCheckMark from "../../assets/icons/GreenCheckMark.png";
import Error from "@mui/icons-material/Error";
import { getPostalPlace } from "./PostalCode";
import * as S from "./Styles";

function ContactForm() {
  const confirmationRef = useRef<HTMLDivElement>(null);
  const postalRequestId = useRef(0);
  const formId = process.env.REACT_APP_FORMSPREE_FORM_ID;
  const [state, handleSubmit] = useForm(formId || "unconfigured-form");

  const formik = useFormik({
    initialValues: {
      Name: "",
      Email: "",
      Phone: "",
      Message: "",
      Address: "",
      PostalCode: "",
      PostalPlace: "",
    },
    validate,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if (state.succeeded) {
      if (confirmationRef.current) {
        confirmationRef.current.scrollIntoView({ behavior: "smooth" });
      }
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-17925675252/zTDbCOy1iMUcEPSx0ONC",
        });
      }
    }
  }, [state.succeeded]);

  if (state.succeeded) {
    return (
      <S.ConfirmationWrapper ref={confirmationRef}>
        <Image src={GreenCheckMark} alt="Success icon" width="80px" height="80px" />
        <S.ConfirmationMessage>
          Takk for din henvendelse! <br /> Vi kontakter deg så fort som mulig.
        </S.ConfirmationMessage>
      </S.ConfirmationWrapper>
    );
  }

  if (!formId) {
    return (
      <S.FormContainer id="contact-form">
        <S.ConfirmationMessage>Kontaktskjemaet er ikke konfigurert.</S.ConfirmationMessage>
      </S.FormContainer>
    );
  }

  return (
    <S.FormContainer id="contact-form">
      <S.Form onSubmit={formik.handleSubmit}>
        {/* Name */}
        <S.Label htmlFor="name">Navn</S.Label>
        <S.Input
          id="name"
          name="Name"
          type="text"
          autoComplete="name"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.Name}
          $error={!!(formik.touched.Name && formik.errors.Name)}
        />
        {formik.touched.Name && formik.errors.Name ? (
          <S.ValidationContainer>
            <Error
              style={{
                color: "red",
                height: "18px",
                width: "18px",
              }}
            />
            <S.ErrorMessage>{formik.errors.Name}</S.ErrorMessage>
          </S.ValidationContainer>
        ) : null}
        <ValidationError prefix="Name" field="Name" errors={state.errors} />
        {/* E-mail */}
        <S.Label htmlFor="email">E-post</S.Label>
        <S.Input
          id="email"
          name="Email"
          type="email"
          autoComplete="email"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.Email}
          $error={!!(formik.touched.Email && formik.errors.Email)}
        />
        {formik.touched.Email && formik.errors.Email ? (
          <S.ValidationContainer>
            <Error
              style={{
                color: "red",
                height: "18px",
                width: "18px",
              }}
            />
            <S.ErrorMessage>{formik.errors.Email}</S.ErrorMessage>
          </S.ValidationContainer>
        ) : null}
        <ValidationError prefix="Email" field="Email" errors={state.errors} />
        {/* Phone */}
        <S.Label htmlFor="phone">Telefon</S.Label>
        <S.Input
          id="phone"
          name="Phone"
          type="tel"
          autoComplete="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.Phone}
        />
        {formik.touched.Phone && formik.errors.Phone ? (
          <S.ValidationContainer>
            <Error
              style={{
                color: "red",
                height: "18px",
                width: "18px",
              }}
            />
            <S.ErrorMessage>{formik.errors.Phone}</S.ErrorMessage>
          </S.ValidationContainer>
        ) : null}
        <ValidationError prefix="Phone" field="Phone" errors={state.errors} />
        {/* Address */}
        <S.Label htmlFor="address">Adresse</S.Label>
        <S.Input
          id="address"
          name="Address"
          type="text"
          autoComplete="address"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.Address}
          $error={!!(formik.touched.Address && formik.errors.Address)}
        />
        {formik.touched.Address && formik.errors.Address ? (
          <S.ValidationContainer>
            <Error
              style={{
                color: "red",
                height: "18px",
                width: "18px",
              }}
            />
            <S.ErrorMessage>{formik.errors.Address}</S.ErrorMessage>
          </S.ValidationContainer>
        ) : null}
        <ValidationError prefix="Address" field="Address" errors={state.errors} />
        {/* Postal Code */}
        <S.PostalWrapper>
          <S.InputContainer>
            <S.Label htmlFor="postalCode">Postnummer</S.Label>
            <S.PostalInput
              id="postalCode"
              name="PostalCode"
              type="text"
              autoComplete="postal-code"
              value={formik.values.PostalCode}
              onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                formik.handleChange(e);
                const value = e.target.value;
                const requestId = ++postalRequestId.current;
                if (value.length === 4 && /^\d{4}$/.test(value)) {
                  const postalPlace = await getPostalPlace(value);
                  if (postalPlace && postalRequestId.current === requestId) {
                    formik.setFieldValue("PostalPlace", postalPlace);
                  }
                } else if (postalRequestId.current === requestId) {
                  formik.setFieldValue("PostalPlace", "");
                }
              }}
              $error={!!(formik.touched.PostalCode && formik.errors.PostalCode)}
            />
            {formik.touched.PostalCode && formik.errors.PostalCode ? (
              <S.ValidationContainer>
                <Error
                  style={{
                    color: "red",
                    height: "18px",
                    width: "18px",
                  }}
                />
                <S.ErrorMessage>{formik.errors.PostalCode}</S.ErrorMessage>
              </S.ValidationContainer>
            ) : null}
            <ValidationError prefix="PostalCode" field="PostalCode" errors={state.errors} />
          </S.InputContainer>
          {/* Postal Address */}
          <S.InputContainer>
            <S.Label htmlFor="postalPlace">Poststed</S.Label>
            <S.PostalOutput
              id="postalPlace"
              name="PostalPlace"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.PostalPlace}
              disabled
            />
          </S.InputContainer>
        </S.PostalWrapper>
        {/* Message */}
        <S.Label htmlFor="message">Din henvendelse</S.Label>
        <S.TextArea
          id="message"
          name="Message"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.Message}
          $error={!!(formik.touched.Message && formik.errors.Message)}
        />
        {formik.touched.Message && formik.errors.Message ? (
          <S.ValidationContainer>
            <Error
              style={{
                color: "red",
                height: "18px",
                width: "18px",
              }}
            />
            <S.ErrorMessage>{formik.errors.Message}</S.ErrorMessage>
          </S.ValidationContainer>
        ) : null}
        <ValidationError prefix="Message" field="Message" errors={state.errors} />
        {/* Submit Button */}
        <Button text="Send" type="submit" isLoading={state.submitting} variant="primary" />
      </S.Form>
    </S.FormContainer>
  );
}

export default ContactForm;
